import { useMemo } from 'react';
import { Route, Routes } from 'react-router-dom';

import { TRoutesTree } from 'types/routes';

/**
 * Функция flattenRoutes рекурсивно проходит по массиву маршрутов,
 * вычисляя для каждого маршрут его полный путь относительно родительского.
 * Возвращается плоский массив объектов с полями { path, element, handle }.
 */
export const flattenRoutes = (routes, parentPath = '') => {
    let flatRoutes = [];

    routes.forEach((route) => {
        // Пропускаем виртуальные элементы (без element) в роутинге
        if (route.element && route.path) {
            const paths = Array.isArray(route.path) ? route.path : [route.path];

            paths.forEach((currentPath) => {
                let fullPath;

                if (!parentPath || parentPath === '/') {
                    fullPath = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;
                } else {
                    fullPath = currentPath.startsWith('/')
                        ? `${parentPath}${currentPath}`
                        : `${parentPath}/${currentPath}`;
                }

                // Если текущий маршрут имеет путь '/' и родительский задан, то fullPath = parentPath
                if (currentPath === '/' && parentPath !== '') {
                    fullPath = parentPath;
                }

                const flatRoute = {
                    path: fullPath,
                    element: route.element,
                    title: route.title,
                    icon: route.icon,
                    custom: route.custom,
                    onClick: route.onClick,
                };

                flatRoutes.push(flatRoute);
            });
        }

        if (route.children && route.children.length) {
            let baseFullPath = parentPath;

            // Для реальных маршрутов используем их путь как базовый для детей
            if (route.element && route.path) {
                const basePath = Array.isArray(route.path) ? route.path[0] : route.path;

                if (!parentPath || parentPath === '/') {
                    baseFullPath = basePath.startsWith('/') ? basePath : `/${basePath}`;
                } else {
                    baseFullPath = basePath.startsWith('/')
                        ? `${parentPath}${basePath}`
                        : `${parentPath}/${basePath}`;
                }

                if (basePath === '/' && parentPath !== '') {
                    baseFullPath = parentPath;
                }
            }

            flatRoutes = flatRoutes.concat(flattenRoutes(route.children, baseFullPath));
        }
    });

    return flatRoutes;
};

// Функция для поиска wrapper и contextProvider в дереве маршрутов
const findWrapperAndProvider = (
    routes: TRoutesTree
): {
    wrapper?: React.ComponentType<{ children?: React.ReactNode; routesTree: TRoutesTree }>;
    contextProvider?: React.ComponentType<{ children?: React.ReactNode }>;
} => {
    for (const route of routes) {
        if (route.wrapper || route.contextProvider) {
            return {
                wrapper: route.wrapper,
                contextProvider: route.contextProvider,
            };
        }
        if (route.children) {
            const found = findWrapperAndProvider(route.children);
            if (found.wrapper || found.contextProvider) {
                return found;
            }
        }
    }
    return {};
};

/**
 * Функция renderRoutes принимает вложенную конфигурацию маршрутов,
 * преобразует её в плоский массив маршрутов и возвращает соответствующие
 * компоненты <Route>.
 */
export const RoutesTree = ({ routesTree }: { routesTree: TRoutesTree }) => {
    const flatRoutes = useMemo(() => flattenRoutes(routesTree), [routesTree]);
    const { wrapper: Wrapper, contextProvider: ContextProvider } = useMemo(
        () => findWrapperAndProvider(routesTree),
        [routesTree]
    );

    const content = useMemo(() => {
        const routes = (
            <Routes>
                {flatRoutes.map((route, index) => (
                    <Route key={index} path={route.path} element={route.element} />
                ))}
            </Routes>
        );

        return Wrapper ? <Wrapper routesTree={routesTree}>{routes}</Wrapper> : routes;
    }, [Wrapper, flatRoutes, routesTree]);

    return ContextProvider ? <ContextProvider>{content}</ContextProvider> : content;
};
